import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Spacer, spacing} from '@nfq/react-grid';

import {Button} from 'UI/components/action/Button';
import {FileCard} from 'UI/components/action/inputs/FileCard';
import {P} from 'UI/components/layout/Text';

import {useUploadInput} from './useUploadInput';
import {Upload} from 'Images/icons';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * The `ComponentProps` interface defines the properties for the `UploadInput` component.
 * This interface specifies the shape of props that can be passed to customize the appearance and behavior of the upload input.
 * It includes optional styling and testing properties to ensure proper component integration and testability.
 */
interface ComponentProps {
    accept: string[];
    /**
     * A CSS class name to apply custom styling to the UploadInput component.
     * This allows for external styling overrides and theme customization.
     */
    className?: string;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing.
     *
     * @default 'UploadInput'
     */
    testId?: string;
    validation: ValidationFunc;
}

/**
 * The `UploadInput` component provides a file upload interface with drag-and-drop functionality and visual feedback.
 * This component renders a styled file input area that accepts multiple files, displays uploaded files in a list format, and provides delete functionality for individual files.
 * The component includes validation states, accessibility features, and integrates with the theme system for consistent styling across the application.
 *
 * @param props            The component props.
 * @param props.accept     An array of accepted MIME types for file uploads, which restricts the types of files that can be selected or dragged into the upload area.
 * @param props.className  A CSS class name to apply custom styling to the UploadInput component for external styling overrides and theme customization.
 * @param props.testId     A unique identifier for testing purposes that is crucial for uniquely identifying components during automated testing scenarios.
 * @param props.validation Validation function to validate the file input.
 * @returns A React element representing the complete file upload interface with drag-and-drop area, file list display, and interactive controls.
 *
 * @example
 * ```tsx
 * <UploadInput
 *     className="custom-upload-style"
 *     testId="document-upload"
 * />
 * ```
 */
const UploadInput = ({accept, className, testId = 'UploadInput', validation}: ComponentProps) => {
    const {colors, fileList, handleChange, handleDragOver, handleFileDelete} = useUploadInput();

    return (
        <Wrapper className={className} data-cy={testId}>
            <Label>
                <LabelText><b>Bitte laden Sie Ihre Dokumente hoch</b></LabelText>
                <LabelText><Color $color={colors.textNeutral}>PDF bevorzugt, max. 10 MB</Color></LabelText>
                <Spacer y={6} />
                <Button as="button" icon={Upload} tabIndex={-1} type="button">Hochladen</Button>
                <Input
                    accept={accept.join(', ')}
                    data-cy={`${testId}-input`}
                    type="file"
                    multiple
                    onChange={handleChange}
                    onDragOver={handleDragOver}
                />
            </Label>
            {fileList.length > 0 && (
                <>
                    <Spacer y={8} />
                    <FileList aria-live="polite">
                        {fileList.map((file, index) => (
                            <FileCard
                                key={file.name}
                                file={file}
                                index={index}
                                validate={validation}
                                onDelete={handleFileDelete}
                            />
                        ))}
                    </FileList>
                </>
            )}
        </Wrapper>
    );
};

UploadInput.displayName = 'UploadInput';

export {UploadInput};

const Wrapper = styled.div``;

const Input = styled.input`
    appearance: none;
    cursor: pointer;
    height: 100%;
    left: 0;
    opacity: 0;
    position: absolute;
    top: 0;
    width: 100%;
`;

const Label = styled.label`
    align-items: center;
    background-color: ${({theme}) => theme.colors.backgroundLight};
    border: 1px dashed ${({theme}) => theme.colors.darkBorder};
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    outline: 1px solid hsl(from ${({theme}) => theme.colors.tertiary} H S L / 0);
    padding: ${spacing(6)} ${spacing(6)};
    position: relative;
    transition: border-color 0.2s ease-in-out, outline 0.2s ease-in-out, background-color 0.2s ease-in-out;
    width: 100%;

    &:has(${Input}:user-invalid) {
        background-color: hsl(from ${({theme}) => theme.colors.errorColor} H S L / 0.3);
        border-color: ${({theme}) => theme.colors.errorColor};
    }

    &:has(${Input}:disabled) {
        background-color: ${({theme}) => theme.colors.textNeutral};
        border-color: ${({theme}) => theme.colors.textNeutral};
    }

    &:has(${Input}:focus-visible) {
        border-color: ${({theme}) => theme.colors.tertiary};
        outline: 1px solid hsl(from ${({theme}) => theme.colors.tertiary} H S L / 1);
    }
`;

const LabelText = P.withComponent('span');

const FileList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
`;